import { ProcedureStatus, GenericStatusInfoTitle, GenericStatusIndicator, DraftWaypointStatus, WaypointStatusName } from '@caresend/types';
import update from 'immutability-helper';
import { hasStatus } from '../dataHelpers/status.js';
import { areThereDifferentDates, formatTimeZoneDate, militaryHourToTime } from './date.js';
import { capitalize } from './string.js';

/**
 * Examples:
 *
 * - Confirmed
 *   - Scheduled for 3/20/2023
 *   - Scheduled for multiple dates
 * - In progress
 *   - 3/20/2023
 *   - Multiple dates
 * - Completed
 *   - On 3/20/2023
 *   - On multiple dates
 */

const prefixDateStringByOrderCardStatusTitle = (dateString, friendlyStatus) => {
  switch (friendlyStatus) {
    case GenericStatusInfoTitle.CONFIRMED:
      return `Scheduled for ${dateString}`;

    case GenericStatusInfoTitle.IN_PROGRESS:
      return capitalize(dateString);

    case GenericStatusInfoTitle.COMPLETED:
      return `On ${dateString}`;

    default:
      return '';
  }
};

const getOrderCardUiFriendlyStatus = procedures => {
  const isOrderComplete = procedures.every(procedure => procedure.status === ProcedureStatus.COMPLETED);
  if (isOrderComplete) return GenericStatusInfoTitle.COMPLETED;
  const inProgress = procedures.some(procedure => procedure.status === ProcedureStatus.COMPLETED);
  if (inProgress) return GenericStatusInfoTitle.IN_PROGRESS;
  return GenericStatusInfoTitle.CONFIRMED;
};
/**
 * Renders info about selected time window(s) for display on cards. If using at
 * the order level, pass `friendlyStatus`. Time zone shows after date if it
 * does not match the user’s.
 *
 * Examples:
 *  - Confirmed
 *   - `['Scheduled for 3/20/2023 PDT', 'Between 8 am and 12 pm']` (one date, one time)
 *   - `['Scheduled for 3/20/2023 PDT']` (one date, multiple times)
 *   - `['Scheduled for multiple dates']` (multiple dates)
 * - In progress
 *   - `['3/20/2023 PDT']` (one date with one or more times)
 *   - `['Multiple dates']` (multiple dates)
 * - Completed
 *   - Same as confirmed, replacing `Scheduled for` with `On`
 */

const getProcedureDateInfoForCard = (procedure, friendlyStatus) => {
  var _a, _b;

  const {
    dateInfo
  } = procedure;
  const firstDateInfo = dateInfo === null || dateInfo === void 0 ? void 0 : dateInfo[0];
  if (!dateInfo || !(firstDateInfo === null || firstDateInfo === void 0 ? void 0 : firstDateInfo.timeInstruction)) return [];
  const hasMultipleDates = areThereDifferentDates(dateInfo);
  /** Generate a friendly status for a single procedure if none passed */

  const friendlyStatusToUse = friendlyStatus || getOrderCardUiFriendlyStatus([procedure]);
  const dateString = hasMultipleDates ? 'multiple dates' : formatTimeZoneDate(firstDateInfo.dateInstruction, 'M/D/YYYY');
  const lines = [prefixDateStringByOrderCardStatusTitle(dateString, friendlyStatusToUse)];

  if (dateInfo.length === 1 && friendlyStatus !== GenericStatusInfoTitle.IN_PROGRESS) {
    const start = militaryHourToTime((_a = firstDateInfo.timeInstruction) === null || _a === void 0 ? void 0 : _a.startTime);
    const end = militaryHourToTime((_b = firstDateInfo.timeInstruction) === null || _b === void 0 ? void 0 : _b.endTime);
    lines.push(`Between ${start} and ${end}`);
  }

  return lines;
};
const getOrderStatusInfo = (order, procedures) => {
  // Future improvement: More accurate statuses
  const firstProcedure = procedures === null || procedures === void 0 ? void 0 : procedures[0];
  if (!order || !firstProcedure) return {
    color: GenericStatusIndicator.GREY,
    title: GenericStatusInfoTitle.DEFAULT,
    subtitle: ''
  };
  const friendlyStatus = getOrderCardUiFriendlyStatus(procedures);
  const dateInfo = getProcedureDateInfoForCard(firstProcedure, friendlyStatus);

  switch (friendlyStatus) {
    case GenericStatusInfoTitle.COMPLETED:
      return {
        title: GenericStatusInfoTitle.COMPLETED,
        subtitle: dateInfo,
        color: GenericStatusIndicator.GREEN
      };

    case GenericStatusInfoTitle.IN_PROGRESS:
      return {
        title: GenericStatusInfoTitle.IN_PROGRESS,
        subtitle: dateInfo,
        color: GenericStatusIndicator.ORANGE
      };

    case GenericStatusInfoTitle.CONFIRMED:
      return {
        title: GenericStatusInfoTitle.CONFIRMED,
        subtitle: dateInfo,
        color: GenericStatusIndicator.BLUE
      };

    default:
      return {
        color: GenericStatusIndicator.GREY,
        title: GenericStatusInfoTitle.DEFAULT,
        subtitle: ''
      };
  }
};
/** Helper function to defined the statusInfo from either a waypoint or a draftWaypoint */

const getWaypointStatusInfo = (draftWaypoint, waypoint, procedureStatuses,
/** Order is being scheduled offline */
offlineScheduling) => {
  if (hasStatus(draftWaypoint, DraftWaypointStatus.CANCELED)) {
    return {
      title: GenericStatusInfoTitle.CANCELED,
      color: GenericStatusIndicator.RED
    };
  }

  const isBrickAndMortarVisit = !!draftWaypoint.drawPlaceID;

  if (isBrickAndMortarVisit && procedureStatuses) {
    const visitScheduledOrPending = hasStatus(draftWaypoint, [DraftWaypointStatus.FINALIZED, DraftWaypointStatus.PENDING]);
    const someProcedureNotCanceledOrCompleted = procedureStatuses.some(status => !hasStatus(status, [ProcedureStatus.CANCELED, ProcedureStatus.COMPLETED]));

    if (visitScheduledOrPending && !someProcedureNotCanceledOrCompleted) {
      return {
        title: GenericStatusInfoTitle.APPOINTMENT_COMPLETE,
        color: GenericStatusIndicator.GREEN
      };
    }
  }

  if (waypoint) {
    switch (waypoint.status.status) {
      case WaypointStatusName.DRAFT:
        return {
          title: GenericStatusInfoTitle.DRAFT,
          color: GenericStatusIndicator.GREY
        };

      case WaypointStatusName.TRANSIT_WAYPOINT:
        return {
          title: GenericStatusInfoTitle.NURSE_ON_THE_WAY,
          color: GenericStatusIndicator.GREEN
        };

      case WaypointStatusName.AT_WAYPOINT:
        return {
          title: GenericStatusInfoTitle.NURSE_AT_PATIENTS,
          color: GenericStatusIndicator.GREEN
        };

      case WaypointStatusName.COMPLETE:
        return {
          title: GenericStatusInfoTitle.VISIT_COMPLETE,
          color: GenericStatusIndicator.GREEN
        };

      case WaypointStatusName.IN_ITINERARY:
        return {
          title: GenericStatusInfoTitle.IN_ITINERARY,
          color: GenericStatusIndicator.CELESTE
        };

      case WaypointStatusName.CREATED:
        return {
          title: GenericStatusInfoTitle.SCHEDULED,
          color: GenericStatusIndicator.PURPLE
        };
    }
  }

  switch (draftWaypoint.status) {
    case DraftWaypointStatus.PENDING:
      /**
       * If order is being scheduled offline, then it has already been sent for
       * scheduling
       */
      if (offlineScheduling) {
        return {
          title: GenericStatusInfoTitle.SENT_FOR_SCHEDULING,
          color: GenericStatusIndicator.PURPLE
        };
      }

      return {
        title: GenericStatusInfoTitle.PENDING_SCHEDULING,
        color: GenericStatusIndicator.YELLOW
      };

    case DraftWaypointStatus.FINALIZED:
    case DraftWaypointStatus.ACCEPTED:
      return {
        title: GenericStatusInfoTitle.SCHEDULED,
        color: GenericStatusIndicator.PURPLE
      };

    case DraftWaypointStatus.REJECTED:
      return {
        title: GenericStatusInfoTitle.REJECTED,
        color: GenericStatusIndicator.RED
      };

    case DraftWaypointStatus.IN_ITINERARY:
      return {
        title: GenericStatusInfoTitle.IN_ITINERARY,
        color: GenericStatusIndicator.CELESTE
      };

    case DraftWaypointStatus.CANCELED:
      return {
        title: GenericStatusInfoTitle.CANCELED,
        color: GenericStatusIndicator.RED
      };
  }
};
/**
 * Strip values other than title and color
 */

const getMinimalStatusInfo = statusInfo => update(statusInfo, {
  $unset: ['callBackButtonLabel', 'subtitle']
});
/**
 * Strip values other than title and color
 */

const getMinimalStatusInfoArray = statusInfo => statusInfo.map(getMinimalStatusInfo);

export { getMinimalStatusInfo, getMinimalStatusInfoArray, getOrderCardUiFriendlyStatus, getOrderStatusInfo, getProcedureDateInfoForCard, getWaypointStatusInfo };
